/**
 * A custom element that displays content based on authentication state.
 *
 * @example
 * ```html
 * <tbw-auth-state>
 *   <div slot="premium">
 *     <tbw-signout-btn></tbw-signout-btn>
 *   </div>
 *   <div slot="free">
 *     <tbw-signin></tbw-signin>
 *   </div>
 * </tbw-auth-state>
 * ```
 */

import type { AuthService } from '../services/auth.service'

export class AuthState extends HTMLElement {
  private slotEl!: HTMLSlotElement
  private handleAuthChange = (): void => {
    this.updateSlot()
  }

  constructor(private authService: AuthService = window.tbwServices.authService) {
    super()
    this.style.width = 'inherit'
    this.style.height = 'inherit'
  }

  connectedCallback() {
    this.attachShadow({ mode: 'open' })
    const style = document.createElement('style')
    style.textContent = `
      ::slotted([hidden]) {
        display: none !important;
      }
    `
    this.slotEl = document.createElement('slot')
    this.shadowRoot!.appendChild(style)
    this.shadowRoot!.appendChild(this.slotEl)
    this.updateSlot()
    document.addEventListener('authChange', this.handleAuthChange)
  }

  disconnectedCallback() {
    document.removeEventListener('authChange', this.handleAuthChange)
  }

  private getUserAccessSlotName(): string {
    return this.authService.isPremium ? 'premium' : 'free'
  }

  private updateSlot(): void {
    this.slotEl.name = this.getUserAccessSlotName()
  }
}
